/**
 * Pure (AssetSource) → {route, sessionId} resolver for the media studio
 * dialog-as-launcher flow (PD-6).
 *
 * The `kind` of the source determines the destination route AND the
 * sessionId on which to call `chat.sendMessage` after navigation.
 *
 * - `chat`: stay on the Home composer with the existing sessionId
 * - `library`: spawn a new session via `crypto.randomUUID()`
 * - `plan-slot` and `calendar-slot`: route to the home chat (PD-2 R4 path),
 *   which reads `planId`/`slotId` (or `calendarSlot`) from URL params and
 *   binds via `assign_post_to_slot`. The handoff store keys these flushes
 *   on the literal sentinel `HOME_HANDOFF_KEY` so a single home-page
 *   consumer can drain them without per-slot session bookkeeping.
 * - `brand-kit`: out of scope for PD-6 v1; throws so callers fail loud.
 */

export type AssetSource =
  | { kind: 'chat'; sessionId: string; messageId?: string }
  | { kind: 'library' }
  | { kind: 'plan-slot'; planId: string; slotId: string }
  | { kind: 'calendar-slot'; slotId: string; scheduledFor: string }
  | { kind: 'brand-kit'; assetType: string };

/** Routing-key sentinel. The home page mounts a chat handoff consumer
 * keyed on this exact string so plan-slot + calendar-slot edits flush
 * into the home chat (where PD-2 R4 already reads planId/slotId from
 * URL params and binds via assign_post_to_slot). */
export const HOME_HANDOFF_KEY = 'home';

export interface RouteForSourceResult {
  route: string;
  /** sessionId on which to call sendMessage. For plan-slot and
   * calendar-slot this is the literal sentinel `HOME_HANDOFF_KEY`
   * ('home'), NOT a uuid. The home page consumer matches this key
   * and drains. NEVER null. */
  sessionId: string;
}

export function routeForSource(source: AssetSource): RouteForSourceResult {
  switch (source.kind) {
    case 'chat':
      return {
        route: `/?session=${encodeURIComponent(source.sessionId)}`,
        sessionId: source.sessionId,
      };
    case 'library': {
      // RLS contract: the uuid is allocated client-side, but the row in
      // ai_sessions is created server-side when the agent processes the
      // first message (the agent route INSERTs with auth.uid() so the
      // session is owned by the authenticated user). The uuid here is
      // just a routing key — it doesn't constitute a session until the
      // server has written it. If the user hard-refreshes before the
      // first message lands, the uuid is orphaned (no row created) and
      // the URL becomes a no-op.
      const id = crypto.randomUUID();
      return { route: `/?session=${id}`, sessionId: id };
    }
    case 'plan-slot':
      return {
        route: `/?planId=${encodeURIComponent(source.planId)}&slotId=${encodeURIComponent(source.slotId)}`,
        sessionId: HOME_HANDOFF_KEY,
      };
    case 'calendar-slot':
      return {
        route: `/?calendarSlot=${encodeURIComponent(source.slotId)}`,
        sessionId: HOME_HANDOFF_KEY,
      };
    case 'brand-kit':
      throw new Error('brand-kit source not in scope for PD-6 v1');
  }
}
